import React, { useMemo } from "react";
import { formatCompactNumber } from "../../../utils";
import { useContainerWidth } from "../../../hooks/useContainerWidth";
import { EnrichedPoint, LayoutRect, TreemapChartProps } from "./types";
import {
  Cell,
  CellInner,
  CellName,
  CellPct,
  CellValue,
  Container,
  EmptyState,
  TitleEl,
} from "./styles";

const GAP = 3;
const MIN_LABEL_WIDTH = 56;
const MIN_LABEL_HEIGHT = 44;

function layoutSorted(
  items: EnrichedPoint[],
  x: number,
  y: number,
  w: number,
  h: number,
  total: number,
): LayoutRect[] {
  if (items.length === 1) return [{ ...items[0], x, y, width: w, height: h }];

  let cumulative = 0;
  let splitIdx = 1;
  let bestDiff = Infinity;
  let aSum = 0;

  for (let i = 0; i < items.length - 1; i++) {
    cumulative += items[i].value;
    const diff = Math.abs(cumulative * 2 - total);
    if (diff < bestDiff) {
      bestDiff = diff;
      splitIdx = i + 1;
      aSum = cumulative;
    }
  }

  const a = items.slice(0, splitIdx);
  const b = items.slice(splitIdx);
  const ratio = aSum / total;

  if (w >= h) {
    const sw = w * ratio;
    return [
      ...layoutSorted(a, x, y, sw, h, aSum),
      ...layoutSorted(b, x + sw, y, w - sw, h, total - aSum),
    ];
  } else {
    const sh = h * ratio;
    return [
      ...layoutSorted(a, x, y, w, sh, aSum),
      ...layoutSorted(b, x, y + sh, w, h - sh, total - aSum),
    ];
  }
}

function computeLayout(
  items: EnrichedPoint[],
  x: number,
  y: number,
  w: number,
  h: number,
  pinnedLastLabel?: string,
): LayoutRect[] {
  if (!items.length) return [];
  if (items.length === 1) return [{ ...items[0], x, y, width: w, height: h }];

  const pinned = pinnedLastLabel
    ? items.find((i) => i.label === pinnedLastLabel)
    : undefined;
  const sortable = pinnedLastLabel
    ? items.filter((i) => i.label !== pinnedLastLabel)
    : items;
  sortable.sort((a, b) => b.value - a.value);
  const ordered = pinned ? [...sortable, pinned] : sortable;

  const total = ordered.reduce((s, i) => s + i.value, 0);
  return layoutSorted(ordered, x, y, w, h, total);
}

export const TreemapChart: React.FC<TreemapChartProps> = ({
  data,
  title,
  height = 400,
  emptyStateMessage = "No data available",
  pinnedLastLabel,
}) => {
  const [containerRef, containerWidth] = useContainerWidth();

  const total = useMemo(() => data.reduce((s, d) => s + d.value, 0), [data]);

  const rects = useMemo(() => {
    if (!containerWidth || !data.length || !total) return [];
    const enriched = data.map((d) => ({
      ...d,
      percentage: (d.value / total) * 100,
    }));
    return computeLayout(
      enriched,
      0,
      0,
      containerWidth,
      height,
      pinnedLastLabel,
    );
  }, [data, containerWidth, height, total, pinnedLastLabel]);

  return (
    <>
      {title && <TitleEl>{title}</TitleEl>}
      <Container $height={height} ref={containerRef}>
        {!data.length && <EmptyState>{emptyStateMessage}</EmptyState>}
        {rects.map((rect) => {
          const cellW = Math.max(rect.width - GAP, 0);
          const cellH = Math.max(rect.height - GAP, 0);
          const showText = cellW > MIN_LABEL_WIDTH && cellH > MIN_LABEL_HEIGHT;
          return (
            <Cell
              key={rect.label}
              style={{
                left: rect.x + GAP / 2,
                top: rect.y + GAP / 2,
                width: cellW,
                height: cellH,
                background: rect.color,
              }}
            >
              {showText && (
                <CellInner>
                  <CellName>{rect.label}</CellName>
                  <CellValue>{formatCompactNumber(rect.value)} €</CellValue>
                  <CellPct>{rect.percentage.toFixed(0)}% of spend</CellPct>
                </CellInner>
              )}
            </Cell>
          );
        })}
      </Container>
    </>
  );
};
